import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Icon } from "@dtw/icons";
import { listKlanten } from "@/features/klanten/klanten.api";
import { fmtNumber } from "@/lib/format";
import { applyParamUpdates } from "@/lib/searchParams";

interface Search {
  q?: string;
  type?: string;
  page?: string;
}

export const Route = createFileRoute("/_shell/klanten/")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    q: typeof s["q"] === "string" ? (s["q"] as string) : undefined,
    type: typeof s["type"] === "string" ? (s["type"] as string) : undefined,
    page: typeof s["page"] === "string" ? (s["page"] as string) : undefined,
  }),
  component: KlantenPage,
});

const VIEWS = [
  { key: "", labelKey: "klanten.view.alle" },
  { key: "klant", labelKey: "klanten.view.klant" },
  { key: "prospect", labelKey: "klanten.view.prospect" },
];

function KlantenPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const page = Number(search.page ?? "1") || 1;

  const query = useQuery({
    queryKey: ["klanten", { q: search.q, type: search.type, page }],
    queryFn: () => listKlanten({ q: search.q, type: search.type, page: String(page), pageSize: "25" }),
    placeholderData: keepPreviousData,
  });
  const result = query.data;
  const rows = result?.data ?? [];
  const totalPages = result ? Math.max(1, Math.ceil(result.total / result.pageSize)) : 1;

  function update(updates: Record<string, string>) {
    void navigate({ search: (prev) => applyParamUpdates(prev, updates) });
  }

  return (
    <div className="app-page" data-screen-label="Backend Klanten">
      <div className="page-header">
        <div>
          <div className="page-header__title">
            <h1>{t("klanten.title")}</h1>
            {result && <span className="meta">{t("common.results", { count: result.total })}</span>}
          </div>
        </div>
        <div className="page-header__actions">
          <button className="dtw-btn dtw-btn--accent">
            <Icon name="plus" size={14} /> {t("action.newKlant")}
          </button>
        </div>
      </div>

      <div className="toolbar">
        <div className="views">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              className={`view-chip${(search.type ?? "") === v.key ? " is-active" : ""}`}
              onClick={() => update({ type: v.key })}
            >
              {t(v.labelKey as never)}
            </button>
          ))}
        </div>
        <span className="dtw-spacer" />
        <div className="dtw-input-group" style={{ width: "240px", height: "var(--control-height-sm)" }}>
          <span className="dtw-input-group__icon">
            <Icon name="search" size={13} />
          </span>
          <input
            placeholder={t("klanten.search")}
            defaultValue={search.q ?? ""}
            onChange={(e) => update({ q: e.target.value })}
          />
        </div>
      </div>

      <div className="panel">
        <div className="panel__body flush">
          <div className="dtw-table-wrap">
            <table className="dtw-table">
              <thead>
                <tr>
                  <th>{t("klanten.col.klant")}</th>
                  <th>{t("klanten.col.kvk")}</th>
                  <th>{t("klanten.col.contact")}</th>
                  <th className="num">{t("klanten.col.abonnementen")}</th>
                  <th>{t("klanten.col.sinds")}</th>
                  <th>{t("klanten.col.type")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((k) => (
                  <tr key={k.id}>
                    {/* De klant is het startpunt: de rij leidt naar het dossier. */}
                    <td>
                      <Link to="/klanten/$id" params={{ id: k.id }} className="dtw-table__cell-stack">
                        <span className="primary">{k.naam}</span>
                        {k.plaats && <span className="secondary t-tertiary">{k.plaats}</span>}
                      </Link>
                    </td>
                    <td className="mono">{k.kvk ?? "—"}</td>
                    <td>
                      {k.primairContact ? (
                        <div className="dtw-table__cell-stack">
                          <span className="primary">{k.primairContact.naam}</span>
                          {k.primairContact.functie && (
                            <span className="secondary t-tertiary">{k.primairContact.functie}</span>
                          )}
                        </div>
                      ) : (
                        <span className="t-tertiary">{t("klanten.contactUnassigned")}</span>
                      )}
                    </td>
                    <td className="num mono">{fmtNumber(k.aantalAbonnementen)}</td>
                    <td className="mono">{k.klantSinds ?? "—"}</td>
                    <td>
                      <span className={`dtw-chip${k.type === "klant" ? " dtw-chip--indigo" : ""}`}>
                        {k.type === "klant" ? t("klanten.type.klant") : t("klanten.type.prospect")}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {result && result.total > 0 && (
          <div className="panel__foot dtw-hstack">
            <span>{t("common.results", { count: result.total })}</span>
            <span className="dtw-spacer" />
            <div className="dtw-hstack" style={{ gap: "var(--space-1)" }}>
              <button
                className="dtw-btn dtw-btn--ghost dtw-btn--icon dtw-btn--sm"
                aria-label={t("common.previous")}
                disabled={page <= 1}
                onClick={() => update({ page: String(page - 1) })}
              >
                <Icon name="chevron-left" size={14} />
              </button>
              <span className="t-mono" style={{ fontSize: "var(--fs-xs)", color: "var(--text-secondary)" }}>
                {page} / {totalPages}
              </span>
              <button
                className="dtw-btn dtw-btn--ghost dtw-btn--icon dtw-btn--sm"
                aria-label={t("common.next")}
                disabled={page >= totalPages}
                onClick={() => update({ page: String(page + 1) })}
              >
                <Icon name="chevron-right" size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
